/**
 * <veylen-carousel> — scroll-snap carousel with arrows and a progress bar.
 *
 * The scrolling itself is native (scroll-snap + overflow), so the track stays
 * usable with touch, trackpad and keyboard even before this script runs.
 */
class VeylenCarousel extends HTMLElement {
  #viewport;
  #raf;

  connectedCallback() {
    this.#viewport = this.querySelector('[data-carousel-viewport]');
    if (!this.#viewport) return;

    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.#page(-1));
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.#page(1));
    this.addEventListener('click', this.#onExpand);
    this.#viewport.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#update();
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.#viewport?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('click', this.#onExpand);
    cancelAnimationFrame(this.#raf);
  }

  /** Expand or collapse a clamped review. */
  #onExpand = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-review-expand]') : null;
    if (!button) return;

    const card = button.closest('[data-carousel-item]');
    if (!card) return;

    const expanded = card.toggleAttribute('data-expanded');
    button.textContent = expanded ? button.dataset.less || 'Show less' : button.dataset.more || 'Read more';
    button.setAttribute('aria-expanded', String(expanded));
    this.#update();
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => this.#update());
  };

  /** Advance by one card, falling back to a viewport-width jump. */
  #page(direction) {
    const card = this.#viewport.querySelector('[data-carousel-item]');
    const gap = Number.parseFloat(getComputedStyle(this.#viewport).columnGap) || 0;
    const step = card ? card.getBoundingClientRect().width + gap : this.#viewport.clientWidth;

    this.#viewport.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  #update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.#viewport;
    const max = scrollWidth - clientWidth;
    const ratio = max > 0 ? scrollLeft / max : 0;

    const bar = this.querySelector('[data-carousel-progress]');
    if (bar instanceof HTMLElement) {
      // Thumb length reflects how much of the track is visible
      const thumb = max > 0 ? Math.max(0.12, clientWidth / scrollWidth) : 1;
      bar.style.setProperty('--vy-progress-size', `${thumb * 100}%`);
      bar.style.setProperty('--vy-progress-offset', `${ratio * (1 - thumb) * 100}%`);
    }

    const prev = this.querySelector('[data-carousel-prev]');
    const next = this.querySelector('[data-carousel-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = scrollLeft <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = scrollLeft >= max - 1;
  }
}

if (!customElements.get('veylen-carousel')) customElements.define('veylen-carousel', VeylenCarousel);
